import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiRequest } from '../utils/api';
import { ThemeToggle } from '../contexts/ThemeContext';
import DocxsAiLogo from '../components/shared/DocxsAiLogo';

const PaymentResultPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  // Flutterwave may return status=successful when redirect_url is frontend directly
  const rawStatus = query.get('payment') || query.get('status');
  const txRef = query.get('tx_ref');
  const [verifying, setVerifying] = useState(Boolean(txRef));
  const [verifyInfo, setVerifyInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function runVerify() {
      if (!txRef) {
        setVerifying(false);
        return;
      }
      try {
        const result = await apiRequest(`payments/verify/${encodeURIComponent(txRef)}`);
        if (!cancelled) setVerifyInfo(result);
      } catch {
        // Webhook may still fulfill; keep UI based on redirect status
      } finally {
        if (!cancelled) setVerifying(false);
      }
    }
    runVerify();
    return () => {
      cancelled = true;
    };
  }, [txRef]);

  const isSuccess =
    verifyInfo?.status === 'successful' ||
    rawStatus === 'success' ||
    rawStatus === 'successful';

  const isShortlist =
    verifyInfo?.product_type === 'hiring_shortlist' ||
    (txRef && String(txRef).includes('_HS_'));

  const successCopy = isShortlist
    ? 'Payment confirmed. Return to Hiring Shortlist and click Compare to run your batch.'
    : 'Your subscription has been updated. Thank you for your payment!';

  const shortlistContinuePath =
    txRef && isSuccess
      ? `/hiring-shortlist?payment=success&tx_ref=${encodeURIComponent(txRef)}`
      : txRef
        ? `/hiring-shortlist?payment=failed&tx_ref=${encodeURIComponent(txRef)}`
        : '/hiring-shortlist';

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center bg-[var(--color-bg)] px-4">
      <div className="absolute top-4 right-4">
        <ThemeToggle compact />
      </div>
      <div className="flex items-center justify-center mb-8 mt-8">
        <DocxsAiLogo className="h-16 w-auto" />
      </div>
      <div className="bg-[var(--color-surface)] rounded-2xl shadow-[var(--shadow-elevated)] p-8 max-w-md w-full flex flex-col items-center border border-[var(--color-border)]">
        <div className="mb-4">
          {verifying ? (
            <div className="w-16 h-16 border-4 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
          ) : isSuccess ? (
            <svg className="w-16 h-16 text-[var(--color-success)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-16 h-16 text-[var(--color-danger)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
        </div>
        <h2
          className={`text-2xl font-bold mb-2 ${
            verifying
              ? 'text-[var(--color-text)]'
              : isSuccess
                ? 'text-[var(--color-success)]'
                : 'text-[var(--color-danger)]'
          }`}
        >
          {verifying ? 'Confirming payment…' : isSuccess ? 'Payment Successful!' : 'Payment Failed'}
        </h2>
        <p className="text-[var(--color-text-secondary)] mb-4 text-center">
          {verifying
            ? 'Please wait while we confirm your payment with Flutterwave.'
            : isSuccess
              ? successCopy
              : 'Something went wrong with your payment. Please try again or contact support.'}
        </p>
        {txRef && (
          <div className="mb-4 text-xs text-[var(--color-text-muted)]">
            Transaction Reference: <span className="font-mono">{txRef}</span>
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-2 w-full">
          {isShortlist ? (
            <button
              type="button"
              onClick={() => navigate(shortlistContinuePath)}
              className="flex-1 px-6 py-2 brand-gradient-btn rounded-lg font-semibold"
            >
              {isSuccess ? 'Continue to Compare' : 'Back to Hiring Shortlist'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => navigate(isShortlist ? shortlistContinuePath : '/dashboard')}
            className={`flex-1 px-6 py-2 rounded-lg font-semibold transition ${
              isShortlist
                ? 'bg-[var(--color-surface-muted)] text-[var(--color-text)] border border-[var(--color-border)] hover:opacity-90'
                : 'brand-gradient-btn'
            }`}
          >
            {isShortlist ? 'Done' : 'Go to Dashboard'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentResultPage;
